import React from "react";
import { CalendarClock, AlertTriangle } from "lucide-react";
import { useList } from "@/hooks/useApi";
import { PageHeader, Loading, EmptyState, StatusBadge, ProgressBar, fmt } from "@/components/common";
import { Card } from "@/components/ui/card";

export default function Planning() {
  const { data: orders, isLoading } = useList("orders", "/orders");
  const today = new Date().toISOString().slice(0, 10);

  if (isLoading) return <Loading />;
  const active = (orders || []).filter((o) => !["Dispatched"].includes(o.status));
  const sorted = [...active].sort((a, b) => (a.delivery_date || "9999") < (b.delivery_date || "9999") ? -1 : 1);

  return (
    <div>
      <PageHeader title="Production Planning" subtitle="Upcoming orders, deadlines & delay flags" testid="planning-header" />
      {!sorted.length ? <EmptyState title="No orders to plan" /> : (
        <div className="space-y-3">
          {sorted.map((o) => {
            const delayed = o.delivery_date && o.delivery_date < today && o.status !== "Completed";
            const dueSoon = o.delivery_date && o.delivery_date >= today && o.delivery_date <= new Date(Date.now() + 3 * 864e5).toISOString().slice(0, 10);
            return (
              <Card key={o.id} className={`p-4 ${delayed ? "border-destructive/40" : dueSoon ? "border-warning/40" : ""}`} data-testid={`plan-row-${o.order_id}`}>
                <div className="flex flex-col md:flex-row md:items-center gap-3">
                  <div className="md:w-56">
                    <div className="font-num font-semibold text-primary">{o.order_id}</div>
                    <div className="text-sm text-muted-foreground truncate">{o.product_name}</div>
                  </div>
                  <div className="flex items-center gap-4 flex-1 text-sm">
                    <div><span className="text-muted-foreground text-xs block">Start</span>{o.production_start_date || "—"}</div>
                    <div className="flex items-center gap-1"><CalendarClock className="w-4 h-4 text-muted-foreground" /><div><span className="text-muted-foreground text-xs block">Delivery</span>{o.delivery_date || "—"}</div></div>
                    <div><span className="text-muted-foreground text-xs block">Daily Target</span><span className="font-num">{fmt(o.daily_target)}</span></div>
                    <div><span className="text-muted-foreground text-xs block">Priority</span>{o.priority}</div>
                  </div>
                  <div className="md:w-52">
                    <div className="flex items-center gap-2"><ProgressBar value={o.completion_pct} /><span className="text-xs font-num w-9">{o.completion_pct}%</span></div>
                    <div className="mt-1.5 flex items-center gap-2">
                      <StatusBadge status={o.status} />
                      {delayed && <span className="text-xs text-destructive font-semibold flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Delayed</span>}
                      {!delayed && dueSoon && <span className="text-xs text-warning font-semibold">Due soon</span>}
                    </div>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
